import {
  data,
  LoaderFunctionArgs,
  Outlet,
  useLoaderData,
  useOutletContext,
} from "react-router";
import invariant from "tiny-invariant";

import { TImportDeposit, TRawReferential } from "@/types";
import { fromImportTypeToReferentialName } from "@/utils/importDeposits";

export async function loader({ params }: LoaderFunctionArgs) {
  const { scoreId, importId } = params;
  invariant(scoreId, "No scoreId found!");
  invariant(importId, "No importId found!");

  const importDepositResponse = await fetch(
    `/api/client/scores/${scoreId}/import_deposits/${importId}/`
  );
  if (importDepositResponse.status !== 200) {
    throw data("Failed to load this Import Deposit", {
      status: importDepositResponse.status,
    });
  }

  const { data: importDeposit } = (await importDepositResponse.json()) as {
    data: TImportDeposit;
  };
  const coefficientReferentialsResponse = await fetch(
    `/api/client/coefficient_referentials/${fromImportTypeToReferentialName(importDeposit.import_type)}`
  );

  if (coefficientReferentialsResponse.status !== 200) {
    throw data(
      "Failed to load the list of coefficient referentials for this Import Type",
      {
        status: coefficientReferentialsResponse.status,
      }
    );
  }
  const { data: rawReferentials } =
    (await coefficientReferentialsResponse.json()) as {
      data: Array<TRawReferential>;
    };

  return { ok: true, rawReferentials };
}

type ContextType = {
  rawReferentials: Array<TRawReferential>;
};

// See https://reactrouter.com/en/main/hooks/use-outlet-context
export function useImportDepositContext() {
  return useOutletContext<ContextType>();
}

function ImportDepositSummary() {
  const { rawReferentials } = useLoaderData<typeof loader>();

  return <Outlet context={{ rawReferentials } satisfies ContextType} />;
}

export default ImportDepositSummary;
